/**
 * Запоминает, что человек трогал последним.
 *
 * Нужно, чтобы окно вырастало из кнопки, которая его открыла. Спросить об
 * этом сам диалог нельзя: он центрируется и про кнопку ничего не знает, а
 * открыть его может и не кнопка — например, пункт меню в строке таблицы или
 * вовсе программа. Поэтому точка запоминается глобально в момент действия.
 *
 * Слушатели ставятся один раз на документ и работают на этапе перехвата,
 * то есть срабатывают раньше любого обработчика, который успеет закрыть или
 * убрать элемент.
 */

export interface ActivationOrigin {
  /** Центр элемента по горизонтали, в координатах окна просмотра. */
  x: number;
  /** Центр элемента по вертикали. */
  y: number;
  width: number;
  height: number;
  /** Отметка времени — старую точку использовать нельзя. */
  at: number;
}

let last: ActivationOrigin | null = null;
let installed = false;

/** Дольше этого точка считается протухшей: связи с действием уже нет. */
const MAX_AGE_MS = 1200;

function remember(target: EventTarget | null, fallback?: { x: number; y: number }) {
  const element = target instanceof Element ? target.closest('button, a, [role="menuitem"]') : null;

  if (element) {
    const rect = element.getBoundingClientRect();

    // Элемент нулевого размера ничего не сообщает о положении — например,
    // скрытая кнопка или элемент, уже убранный из потока.
    if (rect.width > 0 && rect.height > 0) {
      last = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
        width: rect.width,
        height: rect.height,
        at: Date.now(),
      };

      return;
    }
  }

  if (fallback) {
    last = { x: fallback.x, y: fallback.y, width: 0, height: 0, at: Date.now() };
  }
}

export function installActivationOriginTracker(): void {
  if (installed || typeof document === 'undefined') {
    return;
  }

  installed = true;

  document.addEventListener(
    'pointerdown',
    (event) => {
      // Указатель точнее элемента: контекстное меню должно расти из места
      // нажатия, а не из середины длинной строки таблицы.
      remember(event.target, { x: event.clientX, y: event.clientY });
    },
    { capture: true },
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        remember(event.target);
      }
    },
    { capture: true },
  );
}

/**
 * Последняя точка действия — или null, если её нет, она устарела либо
 * оказалась за пределами экрана.
 *
 * Вырастать из точки за краем экрана нельзя: движение будет выглядеть как
 * рывок из ниоткуда, и лучше честно раскрыться из центра.
 */
export function getActivationOrigin(): ActivationOrigin | null {
  if (!last || Date.now() - last.at > MAX_AGE_MS) {
    return null;
  }

  const outside =
    last.x < 0 || last.y < 0 || last.x > window.innerWidth || last.y > window.innerHeight;

  return outside ? null : last;
}
